import type { StagedAction } from './staged-actions/types'
import type { InvokeErrorEntry, UndoErrorEntry } from './staged-action-local-state'

export type ActionStatus = 'staged' | 'invoking' | 'pending' | 'completed' | 'error' | 'undo-error' | 'archived'
export type StagedActionStatusSubject = Pick<StagedAction, 'id'> &
  Partial<Pick<StagedAction, 'archivedAt' | 'lastInvokedAt' | 'metadata' | 'outputData' | 'toolSet'>>

export const CONNECTION_ERROR_CODES = new Set(['SCOPE_CHANGE_REQUIRED', 'CONNECTION_REQUIRED'])

/**
 * How long a completed action stays in the Outbox before it expires out. Long
 * enough to read the row and reach for Undo — 5s was gone before either.
 */
export const COMPLETED_ACTION_DURATION_MS = 10 * 1000

/**
 * The gateway wraps every execution failure with this prefix before it reaches
 * the client, so the panel would otherwise show the internal sentence twice
 * over ("Failed to execute staged action: No linear connection found for user").
 */
const EXECUTION_ERROR_PREFIX = 'Failed to execute staged action: '

/**
 * The toolset/hydrator throw sites raise a bare `Error` with no `errorCode`, so
 * a missing integration never reaches the CONNECTION_ERROR_CODES path above and
 * arrives as this internal sentence. Recognising the shape is a client-side
 * stand-in until those sites are tagged server-side.
 */
const MISSING_CONNECTION_PATTERN = /^no\s+(.+?)\s+connection found for user\.?$/i

/** Long enough to be a stack trace or a payload dump rather than a message. */
const MAX_READABLE_ERROR_LENGTH = 200

const GENERIC_ERROR_MESSAGE = 'Couldn’t run this action. Try again.'

/**
 * Turns a raw server error into something worth showing in the panel: drops the
 * gateway's wrapper, humanises the untagged missing-connection case, and falls
 * back to generic copy when what's left is empty or is clearly a dump.
 */
export function humanizeInvokeError(message: string | undefined, toolSet?: string | null): string {
  const raw = (message ?? '').startsWith(EXECUTION_ERROR_PREFIX)
    ? message!.slice(EXECUTION_ERROR_PREFIX.length).trim()
    : (message ?? '').trim()

  if (!raw) return GENERIC_ERROR_MESSAGE

  const missingConnection = MISSING_CONNECTION_PATTERN.exec(raw)
  if (missingConnection) {
    // Prefer the action's own toolSet; the captured provider is the fallback for
    // actions that don't carry one.
    return getConnectionErrorMessage('CONNECTION_REQUIRED', toolSet || missingConnection[1])
  }

  if (raw.length > MAX_READABLE_ERROR_LENGTH || raw.includes('\n')) return GENERIC_ERROR_MESSAGE

  return raw
}

function formatProviderName(toolSet?: string | null): string {
  if (!toolSet) return 'this integration'
  return toolSet.charAt(0).toUpperCase() + toolSet.slice(1)
}

export function getConnectionErrorMessage(errorCode: string, toolSet?: string | null): string {
  const provider = formatProviderName(toolSet)
  switch (errorCode) {
    case 'SCOPE_CHANGE_REQUIRED':
      return `${provider} needs to be reconnected to run this action.`
    case 'CONNECTION_REQUIRED':
      return `Connect ${provider} to run this action.`
    default:
      return 'A connection is required to run this action.'
  }
}

export function hasOutputError(action: StagedActionStatusSubject): boolean {
  const output = action.outputData as { isError?: boolean } | null
  return output?.isError === true
}

export function isUnread(action: StagedActionStatusSubject): boolean {
  return !action.metadata?.readAt
}

/**
 * The client-side signals for a single action: that action's own invoke/undo
 * error entries and pending-display start. Status derivation is inherently
 * per-action, so the contract takes the entries directly — callers that hold
 * the shared maps look up `action.id` at the callsite.
 */
export interface ActionStatusInputs {
  invokeError?: InvokeErrorEntry
  undoError?: UndoErrorEntry
  pendingStart?: number
}

// Priority: archived → undo-error → error → pending (client window) → completed → invoking → staged.
// `pendingStart` is seeded only after a successful invoke and cleared on undo/archive, so its
// presence is an authoritative client-side marker that survives transient cache misses (gateway
// refetch races, list-endpoint omissions) where `lastInvokedAt`/`outputData` aren't yet observable.
export function getActionStatus(action: StagedActionStatusSubject, inputs: ActionStatusInputs = {}): ActionStatus {
  if (action.archivedAt) return 'archived'
  if (inputs.undoError) return 'undo-error'
  if (inputs.invokeError || hasOutputError(action)) return 'error'
  if (action.outputData != null) {
    if (inputs.pendingStart != null) return 'pending'
    return 'completed'
  }
  if (inputs.pendingStart != null) return 'pending'
  if (action.lastInvokedAt) return 'invoking'
  return 'staged'
}

/**
 * Whether the action still holds an editable draft — the one question every
 * edit surface asks (inline title/body, the Save footer, the scoped chat's
 * Updating chrome), so they all ask it here rather than each re-deciding.
 *
 * `error` counts alongside `staged`: a failed invoke changed nothing remote, so
 * the draft is intact and fixing it is exactly how the user recovers before
 * Retry. Every other status has either already run (`pending`, `completed`,
 * `undo-error`), is running (`invoking`), or is out of the way (`archived`).
 */
export function isEditableActionStatus(status: ActionStatus): boolean {
  return status === 'staged' || status === 'error'
}

export function getPendingStartTime(pendingStart?: number): string | null {
  return typeof pendingStart === 'number' ? new Date(pendingStart).toISOString() : null
}

export function getErrorMessage(action: StagedActionStatusSubject, inputs: ActionStatusInputs = {}): string | null {
  if (inputs.undoError) return inputs.undoError.errorMessage

  const invokeEntry = inputs.invokeError
  if (invokeEntry) {
    if (invokeEntry.errorCode && CONNECTION_ERROR_CODES.has(invokeEntry.errorCode)) {
      return getConnectionErrorMessage(invokeEntry.errorCode, action.toolSet)
    }
    return humanizeInvokeError(invokeEntry.errorMessage, action.toolSet)
  }

  const output = action.outputData as {
    content?: Array<{ type: string; text: string }>
    isError?: boolean
  } | null
  if (output?.isError && output?.content) {
    const textContent = output.content.find((c) => c.type === 'text')
    if (textContent?.text) return humanizeInvokeError(textContent.text, action.toolSet)
  }

  return null
}
